import React, { useState, useEffect } from 'react';
import { Lock, Mail, Eye, EyeOff, LogOut, Inbox, Image, Trash2, Check, X, Plus, Upload, Calendar, User, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { companyInfo } from '../data/mock';

// Mock admin credentials (will be replaced with backend auth)
const ADMIN_CREDENTIALS = {
  email: 'admin@lespeziart.ro',
  password: 'admin123'
};

const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [messages, setMessages] = useState([]);
  const [portfolioImages, setPortfolioImages] = useState([]);

  // Load data from localStorage on mount
  useEffect(() => {
    const savedLogin = localStorage.getItem('adminLoggedIn');
    if (savedLogin === 'true') {
      setIsLoggedIn(true);
    }
    
    // Load messages
    const savedMessages = JSON.parse(localStorage.getItem('contactMessages') || '[]');
    setMessages(savedMessages);
    
    // Load portfolio images
    const savedImages = JSON.parse(localStorage.getItem('portfolioImages') || '[]');
    setPortfolioImages(savedImages);
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (loginData.email === ADMIN_CREDENTIALS.email && loginData.password === ADMIN_CREDENTIALS.password) {
      setIsLoggedIn(true);
      localStorage.setItem('adminLoggedIn', 'true');
      setLoginError('');
    } else {
      setLoginError('Email sau parolă incorectă');
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    localStorage.removeItem('adminLoggedIn');
  };

  const markAsRead = (id) => {
    const updatedMessages = messages.map(msg =>
      msg.id === id ? { ...msg, read: true } : msg
    );
    setMessages(updatedMessages);
    localStorage.setItem('contactMessages', JSON.stringify(updatedMessages));
  };

  const deleteMessage = (id) => {
    const updatedMessages = messages.filter(msg => msg.id !== id);
    setMessages(updatedMessages);
    localStorage.setItem('contactMessages', JSON.stringify(updatedMessages));
  };

  const addPortfolioImage = (imageData) => {
    const newImage = {
      id: Date.now(),
      ...imageData,
      date: new Date().toISOString()
    };
    const updatedImages = [...portfolioImages, newImage];
    setPortfolioImages(updatedImages);
    localStorage.setItem('portfolioImages', JSON.stringify(updatedImages));
  };

  const deletePortfolioImage = (id) => {
    const updatedImages = portfolioImages.filter(img => img.id !== id);
    setPortfolioImages(updatedImages);
    localStorage.setItem('portfolioImages', JSON.stringify(updatedImages));
  };

  const unreadCount = messages.filter(m => !m.read).length;

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <img
              src={companyInfo.logo}
              alt={companyInfo.name}
              className="h-20 w-auto mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-slate-900">Panou Administrator</h1>
            <p className="text-slate-500 mt-2">Conectează-te pentru a accesa panoul</p>
          </div>

          {/* Login form */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <Input
                    id="email"
                    type="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    placeholder="admin@lespeziart.ro"
                    className="pl-10 h-12 rounded-xl"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Parolă</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    placeholder="••••••••"
                    className="pl-10 pr-10 h-12 rounded-xl"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {loginError && (
                <p className="text-red-500 text-sm text-center">{loginError}</p>
              )}

              <Button type="submit" className="w-full h-12 btn-primary text-white rounded-xl font-semibold">
                Conectare
              </Button>
            </form>

            {/* Demo credentials hint */}
            <div className="mt-6 p-4 bg-blue-50 rounded-xl">
              <p className="text-sm text-blue-700 text-center">
                <strong>Demo:</strong> admin@lespeziart.ro / admin123
              </p>
            </div>
          </div>

          <p className="text-center text-slate-500 text-sm mt-6">
            <a href="/" className="text-blue-600 hover:underline">Înapoi la site</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src={companyInfo.logo}
              alt={companyInfo.name}
              className="h-10 w-auto"
            />
            <div>
              <h1 className="font-bold text-slate-900">Panou Administrator</h1>
              <p className="text-sm text-slate-500">{companyInfo.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-slate-600 hover:text-blue-600 text-sm">Vezi site-ul</a>
            <Button onClick={handleLogout} variant="outline" size="sm" className="gap-2">
              <LogOut size={16} />
              Deconectare
            </Button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="container mx-auto px-6 py-8">
        <Tabs defaultValue="messages" className="space-y-6">
          <TabsList className="bg-white p-1 rounded-xl shadow-sm">
            <TabsTrigger value="messages" className="gap-2 rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Inbox size={18} />
              Mesaje
              {unreadCount > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {unreadCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="portfolio" className="gap-2 rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Image size={18} />
              Portofoliu
            </TabsTrigger>
          </TabsList>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="p-6 border-b border-slate-100">
                <h2 className="text-xl font-bold text-slate-900">Mesaje de Contact</h2>
                <p className="text-slate-500 text-sm mt-1">
                  {messages.length} mesaje total, {unreadCount} necitite
                </p>
              </div>

              {messages.length === 0 ? (
                <div className="p-12 text-center">
                  <Inbox className="mx-auto text-slate-300 mb-4" size={48} />
                  <p className="text-slate-500">Nu există mesaje încă</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`p-6 hover:bg-slate-50 transition-colors ${!message.read ? 'bg-blue-50/50' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <User className="text-blue-600" size={18} />
                            </div>
                            <div>
                              <h4 className="font-semibold text-slate-900">{message.name}</h4>
                              <div className="flex items-center gap-4 text-sm text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Phone size={12} />
                                  {message.phone}
                                </span>
                                {message.email && (
                                  <span className="flex items-center gap-1">
                                    <Mail size={12} />
                                    {message.email}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <p className="text-slate-700 mt-3">{message.message}</p>
                          <div className="flex items-center gap-2 mt-3 text-xs text-slate-400">
                            <Calendar size={12} />
                            {new Date(message.date).toLocaleDateString('ro-RO', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!message.read && (
                            <Button
                              onClick={() => markAsRead(message.id)}
                              variant="outline"
                              size="sm"
                              className="gap-1 text-green-600 border-green-200 hover:bg-green-50"
                            >
                              <Check size={14} />
                              Citit
                            </Button>
                          )}
                          <Button
                            onClick={() => deleteMessage(message.id)}
                            variant="outline"
                            size="sm"
                            className="gap-1 text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio">
            <PortfolioManager
              images={portfolioImages}
              onAdd={addPortfolioImage}
              onDelete={deletePortfolioImage}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// Portfolio Manager Component
const PortfolioManager = ({ images, onAdd, onDelete }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newImage, setNewImage] = useState({
    title: '',
    category: 'Acoperișuri',
    location: 'Iași',
    description: '',
    imageUrl: ''
  });

  const categories = ['Acoperișuri', 'Terase', 'Fundații', 'Balcoane'];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newImage.title && newImage.imageUrl) {
      onAdd(newImage);
      setNewImage({
        title: '',
        category: 'Acoperișuri',
        location: 'Iași',
        description: '',
        imageUrl: ''
      });
      setShowAddForm(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Imagini Portofoliu</h2>
            <p className="text-slate-500 text-sm mt-1">
              {images.length} imagini adăugate
            </p>
          </div>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            className="gap-2 btn-primary text-white rounded-xl"
          >
            {showAddForm ? <X size={18} /> : <Plus size={18} />}
            {showAddForm ? 'Anulează' : 'Adaugă imagine'}
          </Button>
        </div>

        {/* Add form */}
        {showAddForm && (
          <form onSubmit={handleSubmit} className="mt-6 p-6 bg-slate-50 rounded-xl space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Titlu proiect *</Label>
                <Input
                  value={newImage.title}
                  onChange={(e) => setNewImage({ ...newImage, title: e.target.value })}
                  placeholder="ex: Acoperiș bloc rezidențial"
                  className="rounded-lg"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Categorie *</Label>
                <select
                  value={newImage.category}
                  onChange={(e) => setNewImage({ ...newImage, category: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Locație</Label>
                <Input
                  value={newImage.location}
                  onChange={(e) => setNewImage({ ...newImage, location: e.target.value })}
                  placeholder="ex: Iași"
                  className="rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label>URL imagine *</Label>
                <Input
                  value={newImage.imageUrl}
                  onChange={(e) => setNewImage({ ...newImage, imageUrl: e.target.value })}
                  placeholder="https://..."
                  className="rounded-lg"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descriere</Label>
              <Input
                value={newImage.description}
                onChange={(e) => setNewImage({ ...newImage, description: e.target.value })}
                placeholder="Scurtă descriere a proiectului"
                className="rounded-lg"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                Anulează
              </Button>
              <Button type="submit" className="btn-primary text-white">
                <Upload size={16} className="mr-2" />
                Salvează
              </Button>
            </div>
          </form>
        )}
      </div>

      {/* Images grid */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        {images.length === 0 ? (
          <div className="p-12 text-center">
            <Image className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-500">Nu există imagini în portofoliu</p>
            <p className="text-slate-400 text-sm mt-1">Adaugă prima imagine folosind butonul de mai sus</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {images.map((image) => (
              <div key={image.id} className="group relative rounded-xl overflow-hidden shadow-md">
                <div className="aspect-[4/3]">
                  <img
                    src={image.imageUrl}
                    alt={image.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x300?text=Imagine+indisponibilă';
                    }}
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <span className="inline-block px-2 py-1 bg-cyan-500 text-white text-xs rounded-full mb-2">
                      {image.category}
                    </span>
                    <h4 className="font-semibold text-white">{image.title}</h4>
                    <p className="text-slate-300 text-sm">{image.location}</p>
                  </div>
                </div>
                <button
                  onClick={() => onDelete(image.id)}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
